import { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import api from './api';
import qs from 'qs';
import { useUser } from './UserContext';
import BusyOverlay from './BusyOverlay';

const SettingsPage = () => {
  const theme = useTheme();
  const { user, setUser } = useUser();
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    password: '',
    repeatPassword: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setForm(prev => ({
        ...prev,
        fullName: user.userFullName || '',
        email: user.userEmail || ''
      }));
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validation
    if (!form.fullName.trim()) {
      setError('Full name is required');
      return;
    }
    if (!form.email.trim()) {
      setError('Email is required');
      return;
    }
    if (form.password && form.password !== form.repeatPassword) {
      setError('Passwords do not match');
      return;
    }

    const data = {
      userGuid: user?.userGuid || '',
      userFullName: form.fullName,
      userEmail: form.email,
    };

    // Only include password if it's being changed
    if (form.password) {
      data.userPassword = form.password;
    }

    try {
      setIsLoading(true);
      const res = await api.post('/useredit', qs.stringify(data), {
        headers: {
          'Authentication': user.authentication,
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });
      setIsLoading(false);
      console.log('Success:', res.data);

      if (res.status === 200) {
        // Update user context with new data
        const updatedUser = {
          ...user,
          userFullName: form.fullName,
          userEmail: form.email
        };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));

        setSuccess('Settings updated successfully!');
        // Clear password fields
        setForm(prev => ({
          ...prev,
          password: '',
          repeatPassword: ''
        }));
      } else {
        setError(res.statusText || 'Update failed');
      }
    } catch (err) {
      setIsLoading(false);
      if (err.response?.status === 409) {
        setError('Email already exists');
      } else {
        setError(err.response?.data || 'Update failed');
      }
      console.error('Settings update error:', err.response?.data || err.message);
    }
  };

  const isFormValid = form.fullName.trim() &&
                     form.email.trim() &&
                     (!form.password || form.password === form.repeatPassword);

  return (
    <>
      <BusyOverlay open={isLoading} message="Updating settings..." />
      <Box sx={{
        marginBottom: 8,
        marginTop: 8,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        position: 'relative',
        zIndex: 1,
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          opacity: 0.1,
          pointerEvents: 'none',
          zIndex: 0
        }}>
          <img src="/mascot.png" alt="" style={{ width: '100%' }} />
        </div>
        <Paper
          elevation={6}
          sx={{
            padding: 4,
            maxWidth: 500,
            width: '90%',
            textAlign: 'center',
            backgroundColor: theme.palette.background,
            color: theme.palette.text.primary
          }}
        >
          <Box sx={{ marginBottom: 3 }}>
            <Typography sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
              ACCOUNT SETTINGS
            </Typography>
          </Box>

          {error && (
            <Typography variant="body2" color="error" sx={{ marginBottom: 2 }}>
              {error}
            </Typography>
          )}

          {success && (
            <Typography variant="body2" color="success.main" sx={{ marginBottom: 2 }}>
              {success}
            </Typography>
          )}

          <form
            onSubmit={handleSubmit}
            style={{
              marginTop: theme.spacing(2),
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(2),
            }}
          >
            <TextField
              required
              label="Full Name"
              fullWidth
              autoComplete="name"
              value={form.fullName}
              onChange={(e) => setForm({ ...form, fullName: e.target.value })}
              inputProps={{ maxLength: 100 }}
            />
            <TextField
              required
              label="Email"
              type="email"
              autoComplete="email"
              fullWidth
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              inputProps={{ maxLength: 254 }}
            />
            <TextField
              label="New Password (leave blank to keep current)"
              type="password"
              autoComplete="new-password"
              fullWidth
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              inputProps={{ maxLength: 1000 }}
            />
            <TextField
              label="Repeat New Password"
              type="password"
              autoComplete="new-password"
              fullWidth
              value={form.repeatPassword}
              onChange={(e) => setForm({ ...form, repeatPassword: e.target.value })}
              disabled={!form.password}
              inputProps={{ maxLength: 1000 }}
            />

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={!isFormValid}
              style={{ color: theme.palette.primary.contrastText }}
            >
              Update Settings
            </Button>
          </form>
        </Paper>
      </Box>
    </>
  );
};

export default SettingsPage;